/**
 * PortalTransitionAnimator - Handles fade-to-black portal transitions
 *
 * Animation phases:
 * 1. fadeOut: Screen fades to black (300ms)
 * 2. loading: Wait for map to load (while screen is black)
 * 3. fadeIn: Screen fades from black to clear (300ms)
 */

export type PortalTransitionPhase = 'fadeOut' | 'loading' | 'fadeIn' | 'complete';

export interface PortalTransitionCallbacks {
  onFadeOutComplete: () => void | Promise<void>;
  onComplete: () => void;
}

const FADE_OUT_DURATION_MS = 300;
const FADE_IN_DURATION_MS = 300;

export class PortalTransitionAnimator {
  private phase: PortalTransitionPhase = 'complete';
  private alpha = 0; // 0 = transparent, 1 = full black
  private phaseStartTime = 0;
  private animationFrameId: number | null = null;
  private callbacks: PortalTransitionCallbacks | null = null;

  /**
   * Start a portal transition (begins fade out)
   */
  public startTransition(callbacks: PortalTransitionCallbacks): void {
    this.cancelAnimation();
    this.phase = 'fadeOut';
    this.alpha = 0;
    this.phaseStartTime = performance.now();
    this.callbacks = callbacks;
    this.tick();
  }

  /**
   * Called when map has finished loading - starts fade in
   */
  public startFadeIn(): void {
    if (this.phase !== 'loading') {
      console.warn('[PortalTransition] startFadeIn called but not in loading phase');
      return;
    }
    this.phase = 'fadeIn';
    this.alpha = 1;
    this.phaseStartTime = performance.now();
    this.tick();
  }

  /**
   * Cancel any running animation
   */
  public cancelAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.phase = 'complete';
    this.alpha = 0;
    this.callbacks = null;
  }

  /**
   * Check if a transition is in progress
   */
  public isActive(): boolean {
    return this.phase !== 'complete';
  }

  /**
   * Get the current phase
   */
  public getPhase(): PortalTransitionPhase {
    return this.phase;
  }

  /**
   * Get the current overlay alpha (0-1)
   */
  public getAlpha(): number {
    return this.alpha;
  }

  /**
   * Ease-in cubic for fade out (accelerating)
   */
  private easeInCubic(t: number): number {
    return t * t * t;
  }

  /**
   * Ease-out cubic for fade in (decelerating)
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Animation frame tick
   */
  private tick = (): void => {
    const now = performance.now();
    const elapsed = now - this.phaseStartTime;

    switch (this.phase) {
      case 'fadeOut': {
        const progress = Math.min(1, elapsed / FADE_OUT_DURATION_MS);
        this.alpha = this.easeInCubic(progress);

        if (progress >= 1) {
          this.alpha = 1;
          this.phase = 'loading';
          void Promise.resolve(this.callbacks?.onFadeOutComplete()).catch((error) => {
            console.error('[PortalTransition] Fade-out completion callback failed', error);
          });
          // Don't continue animation - wait for startFadeIn() to be called
          return;
        }
        break;
      }

      case 'fadeIn': {
        const progress = Math.min(1, elapsed / FADE_IN_DURATION_MS);
        this.alpha = 1 - this.easeOutCubic(progress);

        if (progress >= 1) {
          this.alpha = 0;
          this.phase = 'complete';
          const onComplete = this.callbacks?.onComplete;
          this.callbacks = null;
          this.animationFrameId = null;
          onComplete?.();
          return;
        }
        break;
      }

      case 'loading':
        // Waiting for map load - don't animate
        return;

      case 'complete':
        return;
    }

    // Continue animation
    this.animationFrameId = requestAnimationFrame(this.tick);
  };

  /**
   * Draw the fade overlay on a canvas context
   */
  public drawOverlay(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): void {
    if (this.alpha <= 0) return;

    ctx.save();
    ctx.fillStyle = `rgba(0, 0, 0, ${this.alpha})`;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}
